// Mobilde cift dokunma, pinch zoom ve overlay scroll bleed davranislarini merkezden yonetir.

export function installGlobalZoomPrevention() {
  if (typeof document === "undefined") return;

  let lastTouchEnd = 0;
  const touchTargets = "button, [role='button'], a";
  const editableTargets = "input, textarea, select, [contenteditable='true'], [contenteditable='']";

  document.addEventListener(
    "touchend",
    (event) => {
      const now = Date.now();
      const touchedEditable = event.target?.closest?.(editableTargets);

      // iOS Safari'de caret/focus davranisini bozmamak icin form alanlarinda default davranisi koru.
      if (touchedEditable) {
        lastTouchEnd = now;
        return;
      }

      const tappedInteractive = event.target?.closest?.(touchTargets);

      if (tappedInteractive && now - lastTouchEnd <= 300) {
        event.preventDefault();
      }

      lastTouchEnd = now;
    },
    { passive: false }
  );
}

export function installMobileOverlayScrollLock() {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const hasTouchInput =
    (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) ||
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0;

  if (!hasTouchInput) return;

  const activeOverlaySelector = ".modal.show, .c-sidebar-backdrop";
  let lastTouchX = 0;
  let lastTouchY = 0;
  let lockedScrollY = 0;
  let isBodyLocked = false;

  const getScrollableAxes = (element) => {
    if (!(element instanceof HTMLElement)) {
      return { x: false, y: false };
    }

    const style = window.getComputedStyle(element);
    const overflowX = style.overflowX;
    const overflowY = style.overflowY;

    return {
      x:
        ["auto", "scroll", "overlay"].includes(overflowX) &&
        element.scrollWidth > element.clientWidth + 1,
      y:
        ["auto", "scroll", "overlay"].includes(overflowY) &&
        element.scrollHeight > element.clientHeight + 1,
    };
  };

  const findOverlayRoot = (element) => {
    if (!(element instanceof Element)) return null;
    return element.closest(".modal.show, .aw-overlay-sidebar, .aw-filter-sidebar--overlay, .c-sidebar-backdrop");
  };

  const findScrollableAncestor = (element, axis) => {
    let current = element instanceof Element ? element : null;

    while (current && current !== document.body) {
      const scrollable = getScrollableAxes(current);

      if ((axis === "x" && scrollable.x) || (axis === "y" && scrollable.y)) {
        return current;
      }

      if (findOverlayRoot(current)) {
        current = current.parentElement;
        continue;
      }

      current = current.parentElement;
    }

    return null;
  };

  const lockBodyScroll = () => {
    if (isBodyLocked) return;

    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    document.documentElement.classList.add("aw-overlay-scroll-locked");
    document.body.classList.add("aw-overlay-scroll-locked");
    document.body.style.top = `-${lockedScrollY}px`;
    isBodyLocked = true;
  };

  const unlockBodyScroll = () => {
    if (!isBodyLocked) return;

    document.documentElement.classList.remove("aw-overlay-scroll-locked");
    document.body.classList.remove("aw-overlay-scroll-locked");
    document.body.style.top = "";
    window.scrollTo(0, lockedScrollY);
    isBodyLocked = false;
  };

  const syncBodyScrollLock = () => {
    if (document.querySelector(activeOverlaySelector)) {
      lockBodyScroll();
      return;
    }

    unlockBodyScroll();
  };

  const observer = new MutationObserver(() => {
    syncBodyScrollLock();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "style"],
  });

  document.addEventListener(
    "touchstart",
    (event) => {
      if (!document.querySelector(activeOverlaySelector) || event.touches.length !== 1) return;
      lastTouchX = event.touches[0].clientX;
      lastTouchY = event.touches[0].clientY;
    },
    { passive: true }
  );

  document.addEventListener(
    "touchmove",
    (event) => {
      if (!document.querySelector(activeOverlaySelector) || event.touches.length !== 1) return;

      const touchTarget = event.target;
      const overlayRoot = findOverlayRoot(touchTarget);

      if (!overlayRoot) {
        event.preventDefault();
        return;
      }

      const currentTouchX = event.touches[0].clientX;
      const currentTouchY = event.touches[0].clientY;
      const deltaX = currentTouchX - lastTouchX;
      const deltaY = currentTouchY - lastTouchY;

      lastTouchX = currentTouchX;
      lastTouchY = currentTouchY;

      const primaryAxis = Math.abs(deltaX) > Math.abs(deltaY) ? "x" : "y";
      const fallbackAxis = primaryAxis === "x" ? "y" : "x";
      const primaryScrollContainer = findScrollableAncestor(
        touchTarget,
        primaryAxis
      );
      const fallbackScrollContainer = findScrollableAncestor(
        touchTarget,
        fallbackAxis
      );
      const scrollContainer = primaryScrollContainer || fallbackScrollContainer;

      if (!scrollContainer) {
        event.preventDefault();
        return;
      }

      const activeAxis = primaryScrollContainer ? primaryAxis : fallbackAxis;

      if (activeAxis === "x") {
        const isAtLeft = scrollContainer.scrollLeft <= 0;
        const isAtRight =
          scrollContainer.scrollLeft + scrollContainer.clientWidth >= scrollContainer.scrollWidth - 1;

        if ((isAtLeft && deltaX > 0) || (isAtRight && deltaX < 0)) {
          event.preventDefault();
        }
        return;
      }

      const isAtTop = scrollContainer.scrollTop <= 0;
      const isAtBottom =
        scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 1;

      if ((isAtTop && deltaY > 0) || (isAtBottom && deltaY < 0)) {
        event.preventDefault();
      }
    },
    { passive: false }
  );

  window.addEventListener("pagehide", unlockBodyScroll);
  syncBodyScrollLock();
}
